import React from "react";

/**
 * The Veyso mark: an ember tile with a white disc struck out of it.
 *
 * One definition rather than a handful of hand-drawn copies, because the two
 * places this appears are the header and the line telling someone which
 * toolbar button to click — and a mark that has drifted from the icon it is
 * pointing at is worse than no picture at all.
 *
 * The proportions are the icon's, measured from the artwork: the disc is 53% of
 * the tile, the corners 22%. Both are expressed as fractions of `size` so the
 * mark stays the same shape wherever it is drawn.
 *
 * The brand fill exactly (`ember-500`), not the `ember-400` step the rest of
 * the interface uses for control marks. That step exists because an orange
 * shape carrying meaning has to clear 3:1 against the page on its own and
 * #ff6900 misses at 2.89 — but this is the logo, where the colour is the thing
 * being identified, and a mark repainted to pass a contrast rule is identifying
 * something else.
 */
export const VeysoMark: React.FC<{
  size?: number;
  /** Pulses the tile — used in the header while something is actually running. */
  live?: boolean;
}> = ({ size = 14, live }) => {
  const disc = Math.round(size * 0.53);
  const radius = `${(size * 0.22).toFixed(1)}px`;

  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center align-middle"
      style={{ width: size, height: size }}
    >
      {live ? (
        <span className="absolute inset-0 animate-ping bg-ember-500 opacity-60" style={{ borderRadius: radius }} />
      ) : null}
      <span
        className="relative inline-flex items-center justify-center bg-ember-500"
        style={{ width: size, height: size, borderRadius: radius }}
      >
        <span className="rounded-full bg-white" style={{ width: disc, height: disc }} />
      </span>
    </span>
  );
};
